/**
 * TanStack Query hooks for audit report generation and retrieval.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { auditApiService, type CreateAuditReportRequest } from "@/services/auditApi"

const auditKeys = {
  all: ["audit"] as const,
  reports: () => [...auditKeys.all, "reports"] as const,
  reportDetail: (reportId: string) => [...auditKeys.all, "report", reportId] as const,
}

/**
 * Creates an audit report. Returns the job immediately (202). The caller should poll the report
 * status via useAuditReport to track completion.
 */
export function useCreateAuditReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      request,
      idempotencyKey,
    }: {
      request: CreateAuditReportRequest
      idempotencyKey: string
    }) => auditApiService.createReport(request, idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: auditKeys.reports() })
    },
  })
}

/**
 * Fetches a single audit report job status. Polls every 5s while IN_PROGRESS.
 */
export function useAuditReport(reportId: string | null) {
  return useQuery({
    queryKey: auditKeys.reportDetail(reportId ?? ""),
    queryFn: () => auditApiService.getReport(reportId!),
    enabled: !!reportId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.status === "IN_PROGRESS") return 5_000
      return false
    },
  })
}

/**
 * Fetches the current user's audit reports list.
 */
export function useAuditReportsList() {
  return useQuery({
    queryKey: auditKeys.reports(),
    queryFn: () => auditApiService.listReports({ maxResults: 20 }),
  })
}
